import React, { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import { EditIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Button from "components/Button";
import IconButton from "components/IconButton";
import NoPrint from "components/NoPrint";
import { EMPTY_VALUE_REGEX } from "components/RichTextEditor/constants";
import SanitizeHtml from "components/SanitizeHtml";
import Stack from "components/Stack";
import Text from "components/Text";

import MemoSectionContextButton from "entities/Memo/components/Sections/ContextButton";
import NASection from "entities/Memo/components/Sections/NASection";
import SectionContent from "entities/Memo/components/Sections/SectionContent";
import SectionHeader from "entities/Memo/components/Sections/SectionHeader";
import { IMemoRisk, IMemoSection } from "entities/Memo/sdk";

import RiskAdditionDialogButton from "./RiskAdditionDialogButton";
import RiskEditDialog from "./RiskEditDialog";
import RiskReorderDialog from "./RiskReorderDialog";

interface IRisksAndMitigants {
  section: IMemoSection;
  dealId: number;
  onUpdate: () => void;
  externalToken?: string;
}

const RisksAndMitigants = ({
  dealId,
  section,
  externalToken,
  onUpdate,
}: IRisksAndMitigants) => {
  const isShareableMemo = useMemo(
    () => !_.isUndefined(externalToken),
    [externalToken]
  );

  const [riskData, setRiskData] = useState<IMemoRisk | null>(null);
  const [isRiskReorderDialogOpen, setIsRiskReorderDialogOpen] =
    useState<boolean>(false);

  const onRiskReorderDialogClose = useCallback(() => {
    setIsRiskReorderDialogOpen(false);
  }, [setIsRiskReorderDialogOpen]);

  const handleSetRiskData = useCallback(
    ({ riskData }: { riskData: IMemoRisk }) => {
      setRiskData(riskData);
    },
    [setRiskData]
  );

  const onRiskEditClick = useCallback(
    ({ riskData }: { riskData: IMemoRisk }) => {
      setRiskData(riskData);
    },
    [setRiskData]
  );

  return (
    <>
      <SectionHeader
        label={section.title}
        alignActionButton="right"
        actionButton={
          !isShareableMemo && (
            <NoPrint>
              <Stack direction="row" spacing={1}>
                {!section?.is_na && (
                  <>
                    <RiskAdditionDialogButton
                      dealId={dealId}
                      memoSectionId={section.section_id}
                      onUpdate={onUpdate}
                      createCallback={onRiskEditClick}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setIsRiskReorderDialogOpen(true)}
                    >
                      Manage & reorder
                    </Button>
                  </>
                )}
                <MemoSectionContextButton
                  dealId={dealId}
                  section={section}
                  onUpdate={onUpdate}
                />
              </Stack>
            </NoPrint>
          )
        }
      />
      <SectionContent>
        {section?.is_na ? (
          <NASection />
        ) : (
          <Stack spacing={2}>
            {_.isEmpty(section.risks) ? (
              <Text sx={{ color: colors.gray60 }}>No items added yet...</Text>
            ) : (
              section.risks.map((risk) => (
                <React.Fragment key={risk.id}>
                  <SectionHeader
                    label={risk.title}
                    actionButton={
                      !isShareableMemo && (
                        <NoPrint>
                          <IconButton>
                            <EditIcon
                              sx={{
                                cursor: "pointer",
                                width: "24px",
                                height: "24px",
                              }}
                              onClick={() =>
                                onRiskEditClick({ riskData: risk })
                              }
                            />
                          </IconButton>
                        </NoPrint>
                      )
                    }
                    alignActionButton="right"
                  />
                  {EMPTY_VALUE_REGEX.test(risk.description) ||
                  risk.description === "" ? (
                    <Text
                      variant="text2"
                      sx={{
                        color: colors.gray60,
                        padding: theme.spacing(0, 2),
                      }}
                    >
                      No content yet..
                    </Text>
                  ) : (
                    <Text
                      variant="text2"
                      sx={{
                        padding: theme.spacing(0, 2),
                      }}
                    >
                      <SanitizeHtml html={risk.description} />
                    </Text>
                  )}
                </React.Fragment>
              ))
            )}

            <RiskEditDialog
              sectionTitle={section.title}
              open={!_.isNil(riskData)}
              memoSectionId={section.section_id}
              onUpdate={onUpdate}
              riskData={riskData}
              handleSetRiskData={handleSetRiskData}
              onClose={() => setRiskData(null)}
            />

            <RiskReorderDialog
              sectionTitle={section.title}
              open={isRiskReorderDialogOpen}
              onUpdate={onUpdate}
              memoSectionId={section.section_id}
              risks={section.risks}
              onClose={onRiskReorderDialogClose}
            />
          </Stack>
        )}
      </SectionContent>
    </>
  );
};

export default RisksAndMitigants;
